import React, { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowRight, Lock } from 'lucide-react';
import { useAuth } from '@/lib/auth';

const ResetPassword: React.FC = () => {
  const { user, loading, updatePassword } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const passwordMismatch = useMemo(() => confirmPassword.length > 0 && confirmPassword !== password, [confirmPassword, password]);

  if (!loading && !user) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (passwordMismatch) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await updatePassword(password);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E8E8E8] shadow-[0_20px_70px_-28px_rgba(0,0,0,0.14)] p-7 sm:p-9">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-semibold font-poppins text-[#1A1A1A]">Set a new password</h1>
          <p className="text-[#555555] mt-2 text-sm">Choose a new password for your account.</p>
        </div>

        {done ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              Password updated successfully. You can now sign in with your new password.
            </div>
            <Link
              to="/login"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#121212] py-3 text-sm font-semibold text-white hover:bg-[#2A2A2A] transition-colors"
            >
              Go to sign in
              <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="reset-password" className="block text-sm font-medium text-[#555555] mb-1.5">
                New password
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A3A3A3] pointer-events-none"
                  size={18}
                  strokeWidth={1.75}
                />
                <input
                  id="reset-password"
                  type="password"
                  value={password}
                  minLength={8}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 text-[15px] text-[#1A1A1A] placeholder:text-[#A3A3A3] bg-[#FAFAFA] border border-[#E0E0E0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#121212]/20 focus:border-[#121212] transition-shadow"
                  placeholder="At least 8 characters"
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-[#555555] mb-1.5">
                Confirm password
              </label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                minLength={8}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                className="w-full px-4 py-3 text-[15px] text-[#1A1A1A] placeholder:text-[#A3A3A3] bg-[#FAFAFA] border border-[#E0E0E0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#121212]/20 focus:border-[#121212] transition-shadow"
                placeholder="Re-enter password"
              />
            </div>

            {passwordMismatch && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-100/80 rounded-xl px-4 py-3">
                Passwords do not match.
              </div>
            )}
            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-100/80 rounded-xl px-4 py-3">{error}</div>}

            <button
              type="submit"
              disabled={isSubmitting || passwordMismatch}
              className="w-full bg-[#121212] text-white py-3.5 rounded-xl text-[15px] font-semibold hover:bg-[#2A2A2A] disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              {isSubmitting ? 'Updating password...' : 'Update password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
